'use client';

import { Button } from '@shared/components/shadcn';
import { StarFilled } from '@shared/icons';
import { useState } from 'react';

type RatingProps = {
  value?: number;
  onRatingChange?: (rating: number) => void;
  disabled?: boolean;
} & Omit<React.ComponentProps<'div'>, 'onChange' | 'children'>;

function Rating({ value = 0, onRatingChange, disabled = false, className = '', ...props }: RatingProps) {
  const [hoveredRating, setHoveredRating] = useState<number | null>(null);

  const handleStarHover = (rating: number) => {
    if (disabled) return;
    setHoveredRating(rating);
  };

  const handleMouseLeave = () => {
    if (disabled) return;
    setHoveredRating(null);
  };

  const handleStarClick = (rating: number) => {
    if (disabled) return;
    onRatingChange?.(rating);
  };

  const handleKeyDown = (event: React.KeyboardEvent, rating: number) => {
    if (disabled) return;

    const activationKeys = ['Enter', ' '];
    if (activationKeys.includes(event.key)) {
      event.preventDefault();
      onRatingChange?.(rating);
    }
  };

  const effectiveRating = hoveredRating ?? value;

  return (
    <div className={`flex items-center gap-3 ${className}`} role="radiogroup" aria-label="Rating" onMouseLeave={handleMouseLeave} tabIndex={-1} {...props}>
      {Array.from({ length: 5 }, (_, index) => {
        const starValue = index + 1;
        const isFilled = starValue <= effectiveRating;
        const isSelected = starValue === value;

        return (
          <Button
            key={starValue}
            variant="ghost"
            role="radio"
            aria-checked={isSelected}
            aria-label={`Rate ${starValue} star${starValue > 1 ? 's' : ''}`}
            className={`px-0 ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
            onClick={() => handleStarClick(starValue)}
            onMouseEnter={() => handleStarHover(starValue)}
            onKeyDown={e => handleKeyDown(e, starValue)}
            disabled={disabled}
            data-testid={`rating-star-${starValue}`}
          >
            <StarFilled
              size="regular"
              className={`w-12 h-12 ${isFilled ? 'text-accent-green' : 'text-container-secondary'}`}
              data-testid={isFilled ? `rating-star-filled-${starValue}` : `rating-star-outline-${starValue}`}
            />
          </Button>
        );
      })}
    </div>
  );
}

export { Rating, type RatingProps };
